import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { FileSignature, ShieldCheck, Loader2 } from "lucide-react";

/**
 * Public embedded-signing page. Customer arrives via a link sent from the
 * reservation dashboard. They enter their email (matches the reservation) →
 * we request a short-lived session id from the backend → we embed PandaDoc's
 * iframe using their SDK URL. No PandaDoc account required.
 */
export default function ContractSign() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const preSession = params.get("session") || "";
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState(preSession);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestSession = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/public/contract/${bookingId}/session`,
        { email: email.trim() });
      setSessionId(data.session_id);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't start signing session";
      setError(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // If the pre-shared session id is present, jump straight to the iframe
    if (preSession) setSessionId(preSession);
  }, [preSession]);

  return (
    <section className="min-h-screen bg-ink-900 flex flex-col items-center px-4 py-8">
      <div data-testid="contract-sign-root" className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-cyan label-caps text-[11px]">
            <ShieldCheck size={12} /> Secure e-signature
          </div>
          <h1 className="font-serif text-3xl text-white mt-2">Plenty Rydez rental agreement</h1>
          <p className="text-white/60 text-sm mt-2">
            Review and sign on your phone — no account required.
          </p>
        </div>

        {!sessionId ? (
          <form onSubmit={requestSession} className="glass rounded-2xl p-6 space-y-4">
            <label className="block">
              <div className="label-caps text-[10px] text-white/60 mb-2">Confirm your email</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="contract-sign-email"
                placeholder="you@example.com"
                className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan"
              />
              <div className="text-[11px] text-white/40 mt-2">
                Use the same email your reservation confirmation was sent to.
              </div>
            </label>
            {error && <div data-testid="contract-sign-error" className="text-red-300 text-sm">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="contract-sign-continue"
              className="w-full btn-cream px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <FileSignature size={14} />}
              {loading ? "Opening agreement…" : "Open agreement"}
            </button>
          </form>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white overflow-hidden">
            <iframe
              title="Sign agreement"
              data-testid="contract-sign-iframe"
              src={`https://app.pandadoc.com/s/${sessionId}`}
              className="w-full min-h-[80vh]"
              allow="camera; microphone; fullscreen; clipboard-write"
            />
          </div>
        )}
      </div>
    </section>
  );
}
